import { hasSettingEnableLinesListCardsView, hasSettingEnableLinesListTableView } from '../../../../../util/token.customerAppSettings/scanline';
import { getLocation, getHistory, parseQueryString, clearFromQueryString } from '../../../../utils/location';
import { FILTER_KEYS as DROPDOWN_FILTER_KEYS } from './util.selectedFilters';



/** ************************************
 * IMPORTS
 */

const {
  CUSTOMERS,  // legacy key: customer
  ENTITIES,  // legacy key: entity
  JOBS, // legacy key: job
  LINES,  // legacy key: line
  SURVEY_TYPES,  // legacy key: type
  PMS,  // legacy key: pm
  CTS, // legacy key: technician
	TAGS, // legacy key: tag
  // missing: reportdeliver // will map to textsearch manually
} = DROPDOWN_FILTER_KEYS;

// Hacky
const REPORT_DELIVERY = 'reportdelivery';


/** ************************************
 * GENERAL HELPERS
 */

const isDefined = v => v !== undefined && v !== null;

const isEmptyString = v => typeof v === 'string' && v.trim().length === 0;

const isEmptyArray = v => v instanceof Array && !v.some(_v => isDefined(_v));

const isEmptyObject = v => isDefined(v) && typeof v === 'object' && !(v instanceof Array) && Object.keys(v).length === 0;

const isEmpty = v => !isDefined(v) || isEmptyString(v) || isEmptyArray(v) || isEmptyObject(v);




/** ************************************
 * CONFIG
 */

const KEY_Q = 'q';
const KEY_LADF = 'LADF'; // lines aggs dynamic filters
const KEY_LADR = 'LADR'; // lines aggs date range
const KEY_LAST = 'LAST'; // lines aggs search text 

const URL_FILTER_KEYS = {
  LEGACY: [KEY_Q],
  CURR: [
    KEY_LADF, // lines aggs dynamic filters
    KEY_LADR, // lines aggs date range
    KEY_LAST, // lines aggs search text 
  ], 
}

const KEYS_MAP_LEGACY_TO_CURRENT = {
  customer: CUSTOMERS,
  entity: ENTITIES,
  job: JOBS,
  line: LINES,
  type: SURVEY_TYPES,
  pm: PMS,
  technician: CTS,
	tag: TAGS,
}

const getCurrentKey = (legacyKey) => {
  return KEYS_MAP_LEGACY_TO_CURRENT[legacyKey] || 'UNKNOWN';
}

const mapHasLineValues = (map) => { 
  return !!(map[CUSTOMERS] || map[LINES]);
}

const mapHasJobValues = (map) => { 
  return !!(map[JOBS] || map[ENTITIES] || map[PMS] || map[CTS]);
}

const mapHasSurveyValues = (map) => { 
  return !!(map[SURVEY_TYPES]);
}

const mapHasTagValues = (map) => {
	return !!(map[TAGS]);
}




/** ************************************
 * DOMAIN HELPERS
 */

const shouldCheckForLegacyUrlFilters = () => {
  const hasLinesCardsTab = hasSettingEnableLinesListCardsView();
  const hasLinesTableTab = hasSettingEnableLinesListTableView();
  return !hasLinesCardsTab && hasLinesTableTab;
}

const hasLinesCardsUrlFilters = () => { 
  const location = getLocation();
  const urlParams = parseQueryString(location?.search);
  return URL_FILTER_KEYS.LEGACY.some(key => !isEmpty(urlParams[key]));
}

const hasLinesTableUrlFilters = () => {
  const location = getLocation();
  const urlParams = parseQueryString(location?.search);
  return URL_FILTER_KEYS.CURR.some(key => !isEmpty(urlParams[key]));
};



/** ************************************
 * MAIN - READ
 */
const shouldUpdateUrl = () => {
  return (
    shouldCheckForLegacyUrlFilters() &&
    hasLinesCardsUrlFilters() &&
    !hasLinesTableUrlFilters()
  );
}

const _splitFilter = (filter = '') => {
  const parts = filter.split(':');
  const key = parts.shift();
  const value = parts.join(':');
  return { key, value };
};

const getDecodedLegacyUrlFilters = () => {
  const location = getLocation();
  const urlParams = parseQueryString(location?.search);
  const strFilters = urlParams[KEY_Q] || '';
  const results = strFilters.split('~').map(f => _splitFilter(f));;
  return results;
}



/** ************************************
 * MAIN - WRITE
 */

const _createMapKeyToValues = (decodedFilters = []) => {
  // Start with legacy keys map
  const legacyMap = decodedFilters.reduce((acc, pair = {}) => {
    const { key, value } = pair;
    if (key) {
      acc[key] = acc[key] || {};
      acc[key][value] = value;
    }
    return acc;
  }, {});

  // SPECIAL CASE: reportdelivery
  let searchText;
  if (legacyMap[REPORT_DELIVERY]) { 
    const firstValue = Object.keys(legacyMap[REPORT_DELIVERY])[0]; // ignore n+1 keys
    searchText = `Report Delivery: ${firstValue}`
    delete legacyMap[REPORT_DELIVERY];
  }

  // Change legacy keys to current keys
  const legacyKeys = Object.keys(legacyMap);
  const valuesMap = legacyKeys.reduce((acc, legacyKey) => {
    const currentKey = getCurrentKey(legacyKey);
    if (currentKey && currentKey !== 'UNKNOWN') {
      acc[currentKey] = legacyMap[legacyKey];
    }
    return acc;
  }, {});

  return {
    valuesMap,
    searchText
  };
}

const _push = (_results, filterKey, filterItem) => {
  // eslint-disable-next-line no-param-reassign
  _results[filterKey] = _results[filterKey] || [];
  const exists = _results[filterKey].indexOf(filterItem) > 0;
  if (!exists) {
    _results[filterKey].push(filterItem);
  }
}

const _pushMatches = (_results, valuesMap, availableFilters, { filterKey, getDisplayText = (obj) => obj.name } = {}) => {   
  if (!isEmpty(valuesMap[filterKey]) && availableFilters[filterKey]) {
    availableFilters[filterKey].forEach(filterItem => { 
      const displayText = getDisplayText(filterItem);
      const shouldSelect = displayText === valuesMap[filterKey][displayText];
      if (shouldSelect) { 
        _push(_results, filterKey, filterItem);
      }
    })
  }
};

const _pushLineValues = (_results, valuesMap, availableFilters) => {
  _pushMatches(_results, valuesMap, availableFilters, { filterKey: CUSTOMERS, getDisplayText: (obj) => obj.name });
  _pushMatches(_results, valuesMap, availableFilters, { filterKey: LINES, getDisplayText: (obj) => obj.lineName });
}

const _pushJobValues = (_results, valuesMap, availableFilters) => {
  _pushMatches(_results, valuesMap, availableFilters, { filterKey: JOBS, getDisplayText: (obj) => obj.jobNumber });
  _pushMatches(_results, valuesMap, availableFilters, { filterKey: ENTITIES, getDisplayText: (obj) => obj.name });
  _pushMatches(_results, valuesMap, availableFilters, { filterKey: PMS, getDisplayText: (obj) => obj.pmName });
  _pushMatches(_results, valuesMap, availableFilters, { filterKey: CTS, getDisplayText: (obj) => obj.ctName });
}

const _pushSurveyValues = (_results, valuesMap, availableFilters) => {
  _pushMatches(_results, valuesMap, availableFilters, { filterKey: SURVEY_TYPES, getDisplayText: (obj) => obj.type });
}

const _pushTagValues = (_results, valuesMap, availableFilters) => {
  _pushMatches(_results, valuesMap, availableFilters, { filterKey: TAGS, getDisplayText: (obj) => obj.tag_name });
}

const getUpdatedFiltersPayload = (availableFilters) => { 
  const decodedLegacyFilters = getDecodedLegacyUrlFilters();
  const { valuesMap, searchText } = _createMapKeyToValues(decodedLegacyFilters);
  const newSelectedFilters = {};

  const hasLineValues = mapHasLineValues(valuesMap);
  const hasJobValues = mapHasJobValues(valuesMap);
  const hasSurveyValues = mapHasSurveyValues(valuesMap);
	const hasTagValues = mapHasTagValues(valuesMap);

  if (hasLineValues) {
    _pushLineValues(newSelectedFilters, valuesMap, availableFilters);
  }

  if (hasJobValues) {
    _pushJobValues(newSelectedFilters, valuesMap, availableFilters);
  }

  if (hasSurveyValues) {
    _pushSurveyValues(newSelectedFilters, valuesMap, availableFilters);
  }

	if (hasTagValues) {
		_pushTagValues(newSelectedFilters, valuesMap, availableFilters);
	}

  return { searchText, newSelectedFilters };
}




/** ************************************
 * MAIN - LEGACY FILTER URL PARAMS
 */


const _isQueryStringEmpty = str => {
  const _str = (str || '').trim();
  const emptyValues = ['', '?', '?='];
  return emptyValues.some(v => v === _str);
}


const destroyLegacyFilterUrlParams = () => {
  const location = getLocation();
  const history = getHistory();

  const queryString = clearFromQueryString(KEY_Q, location.search);

  if (history?.pushState) {
    let newUrl = location.href.split('?')[0];
    if (!_isQueryStringEmpty(queryString)) {
      newUrl += queryString;
    }
    if (newUrl !== location.href) {
      history.pushState({}, '', newUrl);
    }
  } else { 
    // fallback
    location.search = !_isQueryStringEmpty(queryString) ? queryString : '';
  }
}





/** ************************************
 * EXPORTS
 */

const FALLBACK_URL_FILTERS_UTIL = {
  isEmpty,
  hasSettingEnableLinesListCardsView,
  shouldUpdateUrl,
  getUpdatedFiltersPayload,
  destroyLegacyFilterUrlParams,
}

export default FALLBACK_URL_FILTERS_UTIL;